/* SPDX-License-Identifier: AGPL-3.0-or-later */

import { Component } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS } from '@angular/material/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { NgIf } from '@angular/common';
import { CreateTournamentDto, TournamentEndpointService } from 'openapi-generated';
import { Router, RouterLink } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { isValidDate } from 'rxjs/internal/util/isDate';
import { CustomDateAdapter } from '../../../adapters/date-adapter';
import {MatCheckbox} from "@angular/material/checkbox";

@Component({
  selector: 'app-tournament-create',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatCardModule,
    FormsModule,
    NgIf,
    ReactiveFormsModule,
    RouterLink,
    MatCheckbox,
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
  ],
  templateUrl: './tournament-create.component.html',
  styleUrl: './tournament-create.component.scss',
})
export class TournamentCreateComponent {
  tournamentForm = new FormGroup({
    name: new FormControl('', [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(200),
      Validators.pattern(/^[\w\s.äÄöÖüÜ\-,]*$/),
    ]),
    registrationEndDate: new FormControl<Date | undefined>(undefined, [Validators.required]),
    registrationEndTime: new FormControl('23:59', [Validators.required]),
    maxParticipants: new FormControl<number | undefined>(undefined, [Validators.required]),
    waitingListEnabled: new FormControl<boolean>(false, []),
    description: new FormControl('', []),
  });

  model: Omit<CreateTournamentDto, 'registrationEnd'> & { registrationEnd: Date } = {
    name: '',
    registrationEnd: new Date(),
    maxParticipants: 64,
    description: '',
  };

  constructor(
    private client: TournamentEndpointService,
    private router: Router,
    private snackBar: MatSnackBar,
    protected dateAdapter: CustomDateAdapter,
  ) {}

  get today() {
    return new Date();
  }

  saveTournament() {
    console.log(this.tournamentForm);

    this.tournamentForm.disable();

    const date = this.tournamentForm.controls.registrationEndDate.value!;
    date.setHours(parseInt(this.tournamentForm.controls.registrationEndTime.value!.split(':')[0]));
    date.setMinutes(
      parseInt(this.tournamentForm.controls.registrationEndTime.value!.split(':')[1]),
    );

    this.client
      .createTournament({
        name: this.tournamentForm.controls.name.value!,
        registrationEnd: date.toISOString()!,
        maxParticipants: this.tournamentForm.controls.maxParticipants.value!,
        description: this.tournamentForm.controls.description.value,
        waitingListActivated: this.tournamentForm.controls.waitingListEnabled.value,
      } as CreateTournamentDto)
      .subscribe({
        next: () => {
          this.snackBar.open('Successfully created new tournament', 'Close', {
            duration: 3000,
          });
          this.router.navigate(['/admin/tournaments']);
        },
        error: async (er: HttpErrorResponse) => {
          this.defaultServiceErrorHandling(er);
          this.tournamentForm.enable();
        },
      });
  }

  createTournament() {
    console.log(this.tournamentForm.controls.registrationEndDate.errors);
    if (this.tournamentForm.valid) {
      this.saveTournament();
    }
  }

  isValidDateString(value: string) {
    return isValidDate(this.dateAdapter.parse(value));
  }

  private defaultServiceErrorHandling(error: HttpErrorResponse) {
    let errorMessage = '';
    console.log(JSON.stringify(error));
    if (typeof error.error === 'object') {
      errorMessage = error.error.errors; // why is it different here (plural instead of singular)?
    } else {
      errorMessage = error.error;
    }
    this.snackBar.open('Error: ' + errorMessage, 'Close', {
      duration: 5000,
    });
  }
}
